/**
 * Broker Agent Switcher Component
 *
 * @fileoverview Toggle control allowing brokers to switch between admin and agent views (US8)
 *
 * @changeHistory
 * - 2025-11-29: Initial creation - T056 dual-role view switcher (GitHub Copilot)
 */

import type { JSX } from 'react';
import './BrokerAgentSwitcher.css';

interface BrokerAgentSwitcherProps {
  currentMode: 'admin' | 'agent';
  onModeChange: (mode: 'admin' | 'agent') => void;
}

/**
 * BrokerAgentSwitcher
 *
 * Provides a visual toggle for brokers to switch between:
 * - Admin mode: Access to agent management, approvals, reporting, plans
 * - Agent mode: Access to personal dashboard and CDA submission
 *
 * @param props - Component props
 * @param props.currentMode - Currently active view mode
 * @param props.onModeChange - Callback when mode changes
 */
const BrokerAgentSwitcher = ({
  currentMode,
  onModeChange,
}: BrokerAgentSwitcherProps): JSX.Element => (
  <div className="broker-switcher">
    <div className="broker-switcher__container">
      <button
        type="button"
        className={`broker-switcher__button ${currentMode === 'admin' ? 'broker-switcher__button--active' : ''}`}
        onClick={() => onModeChange('admin')}
        aria-pressed={currentMode === 'admin'}
        aria-label="Switch to Admin view"
      >
        <svg
          className="broker-switcher__icon"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <span className="broker-switcher__label">Broker</span>
      </button>
      <button
        type="button"
        className={`broker-switcher__button ${currentMode === 'agent' ? 'broker-switcher__button--active' : ''}`}
        onClick={() => onModeChange('agent')}
        aria-pressed={currentMode === 'agent'}
        aria-label="Switch to Agent view"
      >
        <svg
          className="broker-switcher__icon"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
        <span className="broker-switcher__label">Agent</span>
      </button>
    </div>
    <div className="broker-switcher__hint">
      <span className="broker-switcher__hint-text">
        {currentMode === 'admin' ? 'Managing agents & approvals' : 'My personal workspace'}
      </span>
    </div>
  </div>
);

export default BrokerAgentSwitcher;
